/**
 * Phase 4 plan 04-06 — Manual test button (D-21).
 *
 * Calls `useLlmApiKeysStore.test(id)`; on `RateLimitError` surfaces a
 * destructive toast with `retryAfter` seconds (D-24). On success emits the
 * localized success toast.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useOrganizationStore } from '@/stores/organization-store'
import { useLlmApiKeysStore } from '@/stores/llm-api-keys-store'
import { RateLimitError } from '@/services/llm-api-keys'

interface TestKeyButtonProps {
  keyId: string
}

export function TestKeyButton({ keyId }: TestKeyButtonProps) {
  const { t } = useTranslation()
  const { currentOrganization } = useOrganizationStore()
  const testKey = useLlmApiKeysStore((s) => s.test)
  const [isLoading, setIsLoading] = useState(false)

  const handleClick = async () => {
    if (!currentOrganization) return
    setIsLoading(true)
    try {
      await testKey(currentOrganization.id, keyId)
      toast.success(t('settings.llmKeys.testSuccess'))
    } catch (e) {
      if (e instanceof RateLimitError) {
        toast.error(
          t('settings.llmKeys.rateLimited', { seconds: e.retryAfter }),
        )
      } else {
        toast.error(
          e instanceof Error ? e.message : t('settings.llmKeys.testFailed'),
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          {t('settings.llmKeys.testing')}
        </>
      ) : (
        t('settings.llmKeys.test')
      )}
    </Button>
  )
}
